import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router";
import Layout from "@/components/layout";
import { DataTable, DataTableColumn } from "@/components/ui/data-table";
import { useRecordQuotesStore } from "@/modules/quotes/quotesStore";
import {
  QuoteResponse,
  QUOTE_STATUS_DISPLAY_NAMES,
  Permission,
  canEditOwnedEntity,
  isQuoteOwner,
} from "@forgepoint/shared";
import {
  useAuthStore,
  useHasAnyPermission,
  useHasPermission,
} from "@/store/authStore";
import { formatConvertedAmount } from "@/lib/quoteCurrency";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { QuoteEditorController } from "./QuoteEditorController";
import { QuoteCsvExportDialog } from "./components/QuoteCsvExportDialog";
import { useEntitySearch } from "@/lib/useEntitySearch";
import { toast } from "sonner";
import { Loader, Archive, ArchiveRestore } from "lucide-react";
import { DataTableBadge } from "@/components/ui/data-table-badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useUserDisplayNames } from "@/store/teamsStore";

const PAGE_SIZE = 50;

export function QuotesController() {
  const location = useLocation();
  const {
    quotes,
    listLoading,
    listError,
    quoteLoadingStates,
    total,
    fetchQuotes,
    archiveQuote,
    unarchiveQuote,
  } = useRecordQuotesStore();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingQuoteId, setEditingQuoteId] = useState<string | undefined>();
  const [showArchived, setShowArchived] = useState(false);
  const [quoteToArchive, setQuoteToArchive] = useState<QuoteResponse | null>(
    null,
  );
  const [page, setPage] = useState(0);
  const [currentSearch, setCurrentSearch] = useState("");

  const currentUserId = useAuthStore((state) => state.user?.id);
  const canCreateQuotes = useHasAnyPermission(
    Permission.QUOTES_EDIT_OWN,
    Permission.QUOTES_EDIT_ALL,
  );
  const canEditAllQuotes = useHasPermission(Permission.QUOTES_EDIT_ALL);
  const canEditOwnQuotes = useHasPermission(Permission.QUOTES_EDIT_OWN);
  const canEditQuote = (quote: QuoteResponse): boolean =>
    canEditOwnedEntity(
      canEditAllQuotes,
      canEditOwnQuotes,
      !!currentUserId && isQuoteOwner(currentUserId, quote),
    );

  const userDisplayNames = useUserDisplayNames();

  const {
    entitiesArray,
    setDisplayedEntityIds,
    handleSearch: baseHandleSearch,
  } = useEntitySearch<QuoteResponse>({
    entities: quotes,
    fetchEntities: (params) =>
      fetchQuotes({
        ...params,
        includeArchived: showArchived || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      }),
  });

  const handleSearch = useCallback(
    (value: string) => {
      setPage(0);
      setCurrentSearch(value);
      baseHandleSearch(value);
    },
    [baseHandleSearch],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
      fetchQuotes({
        search: currentSearch || undefined,
        includeArchived: showArchived || undefined,
        limit: PAGE_SIZE,
        offset: newPage * PAGE_SIZE,
      }).then((ids) => setDisplayedEntityIds(ids));
    },
    [fetchQuotes, currentSearch, showArchived, setDisplayedEntityIds],
  );

  useEffect(() => {
    const state = location.state as { openQuoteId?: string } | null;
    if (state?.openQuoteId) {
      setEditingQuoteId(state.openQuoteId);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleCreateQuote = () => {
    setShowCreateDialog(true);
  };

  const handleEditQuote = (quoteId: string) => {
    setEditingQuoteId(quoteId);
  };

  const handleQuoteSaved = (_quote: QuoteResponse) => {
    setPage(0);
    fetchQuotes({
      search: currentSearch || undefined,
      includeArchived: showArchived || undefined,
      limit: PAGE_SIZE,
      offset: 0,
    }).then((ids) => setDisplayedEntityIds(ids));
    setShowCreateDialog(false);
    setEditingQuoteId(undefined);
  };

  const handleCancelEdit = () => {
    setShowCreateDialog(false);
    setEditingQuoteId(undefined);
  };

  const handleQuoteArchived = () => {
    setPage(0);
    fetchQuotes({
      search: currentSearch || undefined,
      includeArchived: showArchived || undefined,
      limit: PAGE_SIZE,
      offset: 0,
    }).then((ids) => setDisplayedEntityIds(ids));
  };

  const handleToggleArchived = useCallback(
    (checked: boolean) => {
      setShowArchived(checked);
      setPage(0);
      fetchQuotes({
        search: currentSearch || undefined,
        includeArchived: checked || undefined,
        limit: PAGE_SIZE,
        offset: 0,
      }).then((ids) => setDisplayedEntityIds(ids));
    },
    [fetchQuotes, currentSearch, setDisplayedEntityIds],
  );

  const handleArchive = (quote: QuoteResponse) => {
    setQuoteToArchive(quote);
  };

  const handleConfirmArchive = async () => {
    if (!quoteToArchive) {
      return;
    }

    const archivingQuote = quoteToArchive;
    setQuoteToArchive(null);

    const result = await archiveQuote(archivingQuote.id);
    if (result.success) {
      toast.success("Quote archived");
      const ids = await fetchQuotes({
        search: currentSearch || undefined,
        includeArchived: showArchived || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setDisplayedEntityIds(ids);
    } else {
      toast.error(result.error || "Failed to archive quote");
    }
  };

  const handleUnarchive = async (quote: QuoteResponse) => {
    const result = await unarchiveQuote(quote.id);
    if (result.success) {
      toast.success("Quote unarchived");
      const ids = await fetchQuotes({
        search: currentSearch || undefined,
        includeArchived: showArchived || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setDisplayedEntityIds(ids);
    } else {
      toast.error(result.error || "Failed to unarchive quote");
    }
  };

  const columns: DataTableColumn<QuoteResponse>[] = [
    {
      key: "quoteNumber",
      header: "Quote #",
      type: "number",
      width: 100,
      format: (_value, row) => row.quoteDisplayNumber,
    },
    {
      key: "revision",
      header: "Rev",
      type: "number",
      width: 60,
    },
    {
      key: "name",
      header: "Name",
      type: "string",
    },
    {
      key: "status",
      header: "Status",
      width: 120,
      format: (_value: unknown, quote: QuoteResponse) => {
        if (quote.archivedAt) {
          return <DataTableBadge color="amber">Archived</DataTableBadge>;
        }
        if (quote.status === "FINALIZED") {
          return (
            <DataTableBadge color="green">
              {QUOTE_STATUS_DISPLAY_NAMES[quote.status]}
            </DataTableBadge>
          );
        }
        return (
          <DataTableBadge color="slate">
            {QUOTE_STATUS_DISPLAY_NAMES[quote.status]}
          </DataTableBadge>
        );
      },
    },
    {
      key: "total",
      header: "Total",
      type: "string",
      width: 120,
      align: "right",
      format: (_value: unknown, quote: QuoteResponse) =>
        quote.total ? formatConvertedAmount(quote.total, quote) : "-",
    },
    {
      key: "salesRepUserId",
      header: "Sales Rep",
      type: "string",
      width: 180,
      format: (_value: unknown, quote: QuoteResponse) => {
        if (!quote.salesRepUserId) {
          return "-";
        }
        return userDisplayNames.get(quote.salesRepUserId) ?? "-";
      },
    },
    {
      key: "expirationDate",
      header: "Expiration",
      type: "dateOnly",
      width: 120,
    },
    {
      key: "createdAt",
      header: "Created",
      type: "date",
      width: 120,
    },
    {
      key: "actions",
      header: "",
      width: 50,
      format: (_value: unknown, quote: QuoteResponse) => {
        if (!canEditQuote(quote)) {
          return null;
        }

        const loadingState = quoteLoadingStates.get(quote.id);
        const isArchiving = loadingState?.isLoadingArchive;

        return (
          <div className="flex items-center gap-1">
            {quote.archivedAt ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleUnarchive(quote);
                }}
                disabled={isArchiving}
              >
                {isArchiving ? (
                  <Loader className="h-4 w-4 animate-spin" />
                ) : (
                  <ArchiveRestore className="h-4 w-4" />
                )}
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleArchive(quote);
                }}
                disabled={isArchiving}
              >
                {isArchiving ? (
                  <Loader className="h-4 w-4 animate-spin" />
                ) : (
                  <Archive className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  if (listError) {
    return (
      <Layout breadcrumbs={[{ label: "Quotes", current: true }]}>
        <div className="p-8 text-red-600">
          Error loading quotes: {listError}
        </div>
      </Layout>
    );
  }

  return (
    <>
      <Layout breadcrumbs={[{ label: "Quotes", current: true }]}>
        <div className="flex flex-col h-full">
          <DataTable
            columns={columns}
            data={entitiesArray}
            searchPlaceholder="Search quotes..."
            onChangeSearch={handleSearch}
            className="h-full"
            isLoading={listLoading && entitiesArray.length === 0}
            onCreate={canCreateQuotes ? handleCreateQuote : undefined}
            createButtonText="New Quote"
            onClickRow={(row) => handleEditQuote(row.id)}
            pagination={{
              page,
              pageSize: PAGE_SIZE,
              total,
              onPageChange: handlePageChange,
            }}
            toolbarActions={
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    id="show-archived-quotes"
                    checked={showArchived}
                    onCheckedChange={handleToggleArchived}
                  />
                  <Label
                    htmlFor="show-archived-quotes"
                    className="text-sm whitespace-nowrap"
                  >
                    Show Archived
                  </Label>
                </div>
                <QuoteCsvExportDialog />
              </div>
            }
          />
        </div>
      </Layout>

      {/* Create Quote Sheet */}
      <Sheet open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <SheetContent className="w-full sm:max-w-4xl flex flex-col gap-0 p-0">
          <SheetHeader className="px-6 py-4 border-b">
            <SheetTitle>Create New Quote</SheetTitle>
          </SheetHeader>
          <div className="flex-1 relative overflow-hidden">
            <QuoteEditorController
              onSaved={handleQuoteSaved}
              onCancel={handleCancelEdit}
            />
          </div>
        </SheetContent>
      </Sheet>

      {/* Edit Quote Sheet */}
      <Sheet
        open={!!editingQuoteId}
        onOpenChange={(open) => !open && setEditingQuoteId(undefined)}
      >
        <SheetContent className="w-full sm:max-w-4xl flex flex-col gap-0 p-0">
          <SheetHeader className="px-6 py-4 border-b">
            <SheetTitle>Edit Quote</SheetTitle>
          </SheetHeader>
          <div className="flex-1 relative overflow-hidden">
            {editingQuoteId && (
              <QuoteEditorController
                quoteId={editingQuoteId}
                onSaved={handleQuoteSaved}
                onCancel={handleCancelEdit}
                onArchived={handleQuoteArchived}
                onNavigateToQuote={setEditingQuoteId}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog
        open={!!quoteToArchive}
        onOpenChange={(open) => !open && setQuoteToArchive(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive Quote</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to archive{" "}
              {quoteToArchive?.name ? (
                <>
                  &quot;{quoteToArchive.name}&quot; (Quote #
                  {quoteToArchive.quoteDisplayNumber})
                </>
              ) : (
                <>Quote #{quoteToArchive?.quoteDisplayNumber ?? ""}</>
              )}
              ? You can unarchive it later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmArchive}>
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
